import Link from "next/link";
import { useRouter } from "next/router";
import Img from "next/image";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import Image from "@/components/image";
import { richContentOptions } from "@/components/content-options";
import { useSession } from "@/components/providers/session";
import { format } from "date-fns/format";
import { useState } from "react";
import Modal from "@/components/modal";
import {
  ArrowRightCircleIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { currency } from "@/utils/formats";
import OnlineCoursesListing from "@/components/online-courses-listing";

const Bold = (text) => (
  <strong className="font-medium text-black">{text}</strong>
);

const Heading6 = (node, children) => (
  <h6 className="text-sm font-medium text-brand-600">{children}</h6>
);

const P = (node, children) => (
  <p className="first:flex-1 mt-3 first:mt-0 text-gray-500 text-sm lg:text-sm">
    {children}
  </p>
);

const formatAmount = (amount: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount / 100);

const options = {
  ...richContentOptions,
  renderMark: {
    ...richContentOptions.renderMark,
    [MARKS.BOLD]: Bold,
  },
  renderNode: {
    ...richContentOptions.renderNode,
    [BLOCKS.HEADING_6]: Heading6,
    [BLOCKS.PARAGRAPH]: P,
  },
};

function I({ fields, index = 99 }) {
  if (
    !fields.listingImage ||
    !fields.listingImage.fields ||
    !fields.listingImage.fields.file
  ) {
    return null;
  }
  const aspect =
    fields.listingImage.fields.file.details.image.height /
    fields.listingImage.fields.file.details.image.width;
  const aspectClass =
    aspect === 1
      ? "aspect-w-16 aspect-h-9 md:aspect-1"
      : "aspect-w-16 aspect-h-9 md:aspect-1"; //"aspect-w-12 aspect-h-7";
  const imgClass =
    aspect === 1
      ? "rounded-t-lg shadow object-cover object-center"
      : "rounded-t-lg object-cover object-center shadow";
  return (
    <div className="relative text-base w-full">
      <figure>
        <Link href={fields.url ?? ""} className="cursor-pointer">
          <div className={`${aspectClass}`}>
            <Image
              alt=""
              priority={index < 3}
              id="JHVHJVG"
              fields={fields.listingImage}
              className={imgClass}
              fill
            />
          </div>
        </Link>
      </figure>
    </div>
  );
}

export const CourseItem = ({
  course,
  rest,
  session,
  router,
  cat,
  index,
  interest = false,
}: any) => {
  console.log("course", course);
  const cats = course.fields.categories || [];
  const isVTCT = cats.includes("vtct");
  const isArticle = course.fields.type && course.fields.type === "Article";
  const eventData =
    rest.events && rest.events.length > 0
      ? rest.events.find((x) => x.contentful_id === course.sys.id)
      : {
          events: [],
        };

  console.log("eventData", rest.events);
  if (eventData && eventData.events) {
    eventData.events =
      eventData && eventData.events
        ? eventData.events.filter((x) =>
            x.countStudentRegistrations && x.countStudentRegistrations.aggregate
              ? x.seats - x.countStudentRegistrations.aggregate.count > 0
              : false
          )
        : [];
  }

  const wasPrice =
    eventData && eventData.wasPrice > 0 ? eventData.wasPrice : null;

  const standardPrice =
    eventData && eventData.standardPrice ? eventData.standardPrice : null;

  const eventPrice =
    eventData && eventData.events.length > 0 ? eventData.events[0].price : null;

  const nextPrice =
    eventPrice && standardPrice && eventPrice < standardPrice
      ? `Was: ${formatAmount(standardPrice)} - Now: ${formatAmount(eventPrice)}`
      : wasPrice && eventPrice && eventPrice < wasPrice
      ? `Was: ${formatAmount(wasPrice)} - Now: ${formatAmount(eventPrice)}`
      : eventPrice
      ? `Price: ${formatAmount(eventPrice)}`
      : wasPrice && standardPrice && standardPrice < wasPrice
      ? `Was: ${formatAmount(wasPrice)} - Now: ${formatAmount(standardPrice)}`
      : `Price: ${formatAmount(standardPrice)}`;

  const nextCourse =
    eventData &&
    eventData.events &&
    eventData.events.length > 0 &&
    eventData.events.find((x) => x.registrations.length < x.seats);

  async function getEvent() {
    try {
      const req = await fetch(`/api/course-search/?sysId=${course.sys.id}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const res = await req.json();

      const e = res.event;
      const events = res.event.events.filter(
        (x) => x.registrations.length < x.seats
      );

      session.addCourse({
        ...course,
        event: e,
        package: e && e.package,
        selectedEvent: events && events[0] ? events[0] : null,
      });
      setTimeout(() => {
        window.location.href = "/checkout";
      }, 100);
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div
      className="relative flex flex-col rounded-lg shadow-md shadow-gray-300 overflow-hidden border-l-2 md:border-l-4 border-brand-600"
      key={course.sys.id}
    >
      {!isArticle && nextPrice && nextCourse && (
        <div className="flex mb-2 absolute top-2 right-2 z-10">
          <div className="bg-white relative flex items-center gap-x-2 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-gray-300 transition-all duration-200 hover:shadow-lg hover:shadow-brand-400">
            <span className="font-semibold text-brand-600 ">
              {currency(nextCourse.seat_price)}
            </span>
            <span className="h-4 w-px bg-gray-900/10" aria-hidden="true" />
            <Link
              href={course.fields.url}
              className="flex items-center gap-x-1 glow text-xs font-semibold"
            >
              <span className="absolute inset-0" aria-hidden="true" />
              Next course: {format(new Date(nextCourse.date), "do MMMM")}
              <ChevronRightIcon
                className="-mr-2 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </Link>
          </div>
        </div>
      )}
      {!isArticle && nextPrice && !nextCourse && (
        <div className="flex mb-2 absolute top-2 right-2 z-10">
          <div className="bg-white relative flex items-center gap-x-2 rounded-full px-4 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-brand-600 shadow-lg shadow-gray-300 transition-all duration-200 hover:shadow-lg hover:shadow-brand-400">
            <PhoneIcon
              className="h-4 w-4 flex-shrink-0 text-brand-600"
              aria-hidden="true"
            />
            <span className="h-4 w-px bg-gray-900/10" aria-hidden="true" />
            <a
              href="tel:+443330155117"
              className="flex items-center gap-x-1 glow text-xs font-semibold"
            >
              Call now to schedule
              <ChevronRightIcon
                className="-mr-2 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </a>
          </div>
        </div>
      )}
      {course.fields.listingImage && <I index={index} fields={course.fields} />}
      {isVTCT ? (
        <div className="w-16 h-12 absolute top-0 left-0 z-10 bg-white rounded-br p-4">
          <Img
            src="/logo/vtct.png"
            fill
            alt="VTCT Approved Training At The Cosmetic College"
          />
        </div>
      ) : null}
      <div className="flex-1 bg-white p-4 lg:p-6 flex flex-col justify-between">
        <div className="flex-1 flex flex-col">
          <Link href={course.fields.url}>
            <h3 className="text-base md:text-xl font-bold cursor-pointer text-gray-900">
              {course.fields.title}
            </h3>
          </Link>{" "}
          {course.fields.listingText && (
            <Link href={course.fields.url} className="md:sr-only">
              <div className="mt-3 text-sm cursor-pointer text-gray-500 line-clamp-3 md:sr-only">
                {documentToReactComponents(course.fields.listingText, options)}
              </div>
            </Link>
          )}
          {!isArticle ? (
            <>
              <div className="flex items-center space-x-4 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    getEvent();
                  }}
                  className="w-full md:w-max rounded-md bg-brand-600 px-1 py-2 md:p-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 flex-1"
                >
                  <span className="relative z-[1]">Book now</span>
                </button>
                <Link
                  href={course.fields.url}
                  className="w-full md:w-max rounded-md bg-gray-100 px-1 py-2 md:p-3 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-50 outline outline-2 outline-offset-2 outline-gray-300 transition-colors duration-200 hover:outline-brand-500 flex items-center justify-center flex-1 text-center"
                >
                  <span className="relative z-[1]">Course details</span>
                  <ArrowRightCircleIcon className="w-4 ml-0.5 relative z-[1]" />
                </Link>
              </div>
            </>
          ) : (
            <Link
              href={course.fields.url}
              className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-3"
            >
              Read more
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const OnlineCourseItem = ({ course, cat, setInterested }) => {
  console.log("course", course);
  console.log("cat1", cat);

  const isArticle = course.fields.type && course.fields.type === "Article";

  const amount = course.fields.link.line_items.data.reduce(
    (x, a) => x + a.amount_total,
    0
  );

  const nextPrice = course.fields.data.active
    ? `Price: ${formatAmount(amount)}`
    : `Pre Release Special Price: £${course.fields.data.pre}`;

  const handleClick = (href: string) => {
    gtag("event", "begin_checkout", {
      event_callback: function () {
        window.location.href = href;
      },
    });
  };

  const priceBlock = course.fields.data.was ? (
    <div className="absolute right-1 top-1 bg-brand-600  text-white rounded-full py-1 px-2 shadow shadow-gray-500 z-10">
      <p className="text-sm">
        Was: {formatAmount(course.fields.data.was)} - Now {formatAmount(amount)}
      </p>
    </div>
  ) : (
    <div className="absolute right-1 top-1 bg-brand-600  text-white rounded-full py-1 px-2 shadow shadow-gray-500 z-10">
      <p className="text-sm">{nextPrice}</p>
    </div>
  );

  return (
    <div
      className="relative flex flex-col rounded-lg shadow-lg overflow-hidden"
      key={course.sys.id}
    >
      {!isArticle && nextPrice && <>{priceBlock}</>}
      {course.fields.listingImage && (
        <Link href={course.fields.url}>
          <div className="shrink-0 relative aspect-w-16 aspect-h-9">
            <Image
              className="w-full object-cover"
              fields={course.fields.listingImage}
              fill
              alt={course.fields.title}
            />
          </div>
        </Link>
      )}
      <div className="flex-1 bg-white p-4 lg:p-6 flex flex-col justify-between">
        <div className="flex-1 flex flex-col">
          <p className="text-sm font-medium text-brand-600">
            <Link href={course.fields.url}>
              <span className="hover:underline">{cat || "Course"}</span>
            </Link>
          </p>
          <h3 className="text-xl font-semibold text-gray-900 mt-2">
            {course.fields.title}
          </h3>
          {course.fields.listingText && (
            <div className="mt-3 text-base text-gray-500 flex-1 flex flex-col">
              {documentToReactComponents(course.fields.listingText, options)}
            </div>
          )}

          {course.fields.data.release && !course.fields.data.active && (
            <div className="mt-3 text-base text-gray-500 flex flex-col">
              <p className="text-sm font-medium text-gray-500">
                Release date: {course.fields.data.release}
              </p>
            </div>
          )}

          {!isArticle ? (
            <>
              {!course.fields.data.active && (
                <a
                  href={course.fields.link.url}
                  onClick={(e) => {
                    handleClick(course.fields.link.url);
                  }}
                  className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-transparent rounded-md shadow text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 stretch-link mt-3"
                >
                  Pre Order Now
                </a>
              )}
              {course.fields.data.active && (
                <a
                  href={course.fields.link.url}
                  onClick={(e) => {
                    handleClick(course.fields.link.url);
                  }}
                  className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-transparent rounded-md shadow text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 stretch-link mt-3"
                >
                  Buy
                </a>
              )}
              <Link
                href={course.fields.url}
                className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-3"
              >
                View course details
              </Link>
            </>
          ) : (
            <Link
              href={course.fields.url}
              className="group relative w-full inline-flex items-center justify-center px-5 py-2 border border-gray-200 rounded-md shadow text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 stretch-link mt-3"
            >
              Read more
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

function SeperatedListing({ courses, ...rest }) {
  const router = useRouter();
  const session = useSession();
  const [interested, setInterested] = useState<any>(false);

  const cat = rest.entry && rest.entry.fields && rest.entry.fields.title;

  // const { pre, post } = courses.items.reduce(
  //   (c, a) => {
  //     if (a.fields.categories.includes("online")) {
  //       if (a.fields.data.active) {
  //         return {
  //           pre: c.pre,
  //           post: [...c.post, a],
  //         };
  //       } else {
  //         return {
  //           pre: [...c.pre, a],
  //           post: c.post,
  //         };
  //       }
  //     } else {
  //       return {
  //         pre: [],
  //         post: [...c.post, a],
  //       };
  //     }
  //   },
  //   { pre: [], post: [] }
  // );

  const vtct = courses.items.filter((course) => {
    return course.fields.categories.includes("vtct");
  });

  const nonVtct = courses.items.filter((course) => {
    return (
      !course.fields.categories.includes("vtct") &&
      course.fields.data &&
      course.fields.data.redirected !== true
    );
  });

  let navLinks = [...vtct, ...nonVtct].reduce(
    (a, b) => {
      const tt = b.fields.title;
      const matches = tt.match(/(\d+)/);
      if (matches && matches.length > 0) {
        const num = matches[0];
        if (!a[num]) {
          return {
            ...a,
            [num]: [b],
          };
        } else {
          return {
            ...a,
            [num]: [...a[num], b],
          };
        }
      } else {
        return {
          ...a,
          other: [...a.other, b],
        };
      }
    },
    {
      other: [],
    }
  );

  const vtctRoot = rest.entry.fields.url === "/courses/vtct";

  const collection = vtctRoot
    ? vtct
        .filter((course) => {
          const e = rest.events.find((x) => x.contentful_id === course.sys.id);
          return true;
          // (e && e.events && e.events.length > 0)
          // (e.contentful_id === "5ZhbWaRaurWlhsbfGrbzL" ||
          //   e.contentful_id === "BFgxU1uYpiocXGbCoFI76")) ||
          // (e && e.events && e.events.length > 0)
          // );
        })
        .sort((a, b) => b.fields.level - a.fields.level)
    : vtct;

  const interestCollection = vtctRoot
    ? vtct.filter((course) => {
        const e = rest.events.find((x) => x.contentful_id === course.sys.id);
        return e && e.events && e.events.length === 0;
      })
    : null;

  return (
    <>
      <section className="container mx-auto relative bg-gray-50 pb-20 px-4 sm:px-6 lg:pt-0 lg:pb-28">
        <div className="absolute inset-0">
          <div className="bg-white h-1/3 sm:h-2/3"></div>
        </div>
        <div className="relative max-w-7xl mx-auto flex">
          <div className="hidden lg:block sticky lg:top-0 lg:mr-8 lg:w-1/5 lg:mt-6 lg:-ml-4">
            <h3 className="text-lg font-extrabold text-gray-900 sm:tracking-tight mb-4">
              {cat}
            </h3>
            <ul className="space-y-3">
              {Object.keys(navLinks).map((key) => {
                return (
                  <>
                    {key !== "other" ? (
                      <p className="font-semibold">Level {key}</p>
                    ) : (
                      ""
                    )}
                    {navLinks[key]
                      .sort((a, b) =>
                        a.fields.title.localeCompare(b.fields.title)
                      )
                      .map((course) => {
                        return (
                          <li key={`list-${course.sys.id}`} className="text-xs">
                            <Link
                              href={course.fields.url}
                              className="hover:text-brand-500 transition-colors duration-150"
                            >
                              {course.fields.title}
                            </Link>
                          </li>
                        );
                      })}
                  </>
                );
              })}
            </ul>
          </div>
          <div className="flex-1 lg:pl-6">
            {!vtctRoot && (
              <div className="mb-12 md:mb-16 lg:mt-4 mx-auto lg:max-w-none">
                <h3 className="text-xl font-extrabold text-gray-900 sm:text-3xl sm:tracking-tight lg:text-3xl flex items-start flex-col lg:flex-row">
                  <div className="w-44 relative h-12 mb-4">
                    {/* <IG
                      alt="VTCT Training Courses At The Cosmetic College"
                      fill
                      src="https://images.ctfassets.net/eo68gvto3q0j/1M0VdXobETp4giEjEXVxON/97c2099676dc3066ddfdc057577c3883/image"
                    /> */}
                  </div>
                  <span className="mt-2 ml-3">{cat}</span>
                </h3>
              </div>
            )}
            <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
              {collection && collection.length > 0
                ? collection.map((course, idx) => {
                    if (
                      course.fields &&
                      course.fields.type &&
                      course.fields.type === "Online Course" &&
                      !vtctRoot
                    ) {
                      return (
                        <OnlineCourseItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          cat={cat}
                          setInterested={setInterested}
                        />
                      );
                    }
                    return (
                      <CourseItem
                        key={`item-${course.sys.id}`}
                        index={idx}
                        course={course}
                        rest={rest}
                        cat={cat}
                        session={session}
                        router={router}
                      />
                    );
                  })
                : null}
            </div>
            {interestCollection && interestCollection.length > 0 && (
              <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
                {interestCollection && interestCollection.length > 0
                  ? interestCollection.map((course) => {
                      if (
                        course.fields &&
                        course.fields.type &&
                        course.fields.type === "Online Course"
                      ) {
                        return (
                          <OnlineCourseItem
                            key={`item-${course.sys.id}`}
                            course={course}
                            cat={cat}
                            setInterested={setInterested}
                          />
                        );
                      }
                      return (
                        <CourseItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          rest={rest}
                          cat={cat}
                          session={session}
                          router={router}
                          interest
                        />
                      );
                    })
                  : null}
              </div>
            )}
            {nonVtct && nonVtct.length > 0 && (
              <div className="my-12 md:my-16 mx-auto lg:max-w-none">
                <h3 className="text-xl font-extrabold text-gray-900 sm:text-3xl sm:tracking-tight lg:text-3xl">
                  Accredited Masterclasses & CPD Training Courses
                </h3>
              </div>
            )}
            {nonVtct && nonVtct.length > 0 ? (
              <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
                {nonVtct.map((course) => {
                  if (
                    course.fields &&
                    course.fields.type &&
                    course.fields.type === "Online Course"
                  ) {
                    return (
                      <OnlineCourseItem
                        key={`item-${course.sys.id}`}
                        course={course}
                        cat={cat}
                        setInterested={setInterested}
                      />
                    );
                  }
                  return (
                    <CourseItem
                      key={`item-${course.sys.id}`}
                      course={course}
                      rest={rest}
                      cat={cat}
                      session={session}
                      router={router}
                    />
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </section>
      {interested && (
        <Modal
          open={interested}
          close={() => setInterested(false)}
          xlarge
          title="Register Interest"
          subTitle={
            interested
              ? `Get notified when ${interested.fields.title} is available`
              : null
          }
        >
          <form
            action="https://manage.kmail-lists.com/subscriptions/subscribe?a=XxnURE&g=WgFGLa"
            method="post"
            target="_blank"
          >
            <div className="grid gap-4 grid-cols-1 my-4">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Name</span>
                  <input
                    type="text"
                    // {...form.register("name", { required: true })}
                    name="FIRST_NAME"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your name"
                  />
                </label>
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Email</span>
                  <input
                    type="email"
                    // {...form.register("email", { required: true })}
                    name="EMAIL"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your email"
                  />
                </label>
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Phone number</span>
                  <input
                    type="tel"
                    // {...form.register("phone", { required: true })}
                    name="PHONE_NUMBER"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your phone number"
                  />
                </label>
              </div>
              <div className="col-span-1 flex justify-end">
                <div className="w-64">
                  <button className="w-full flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-600 hover:bg-brand-900">
                    Register interest
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

export default function Listing({ courses, ...rest }) {
  const router = useRouter();
  const session = useSession();
  const [interested, setInterested] = useState<any>(false);

  const cat = rest.entry && rest.entry.fields && rest.entry.fields.title;

  if (
    rest.entry.fields.url === "/courses/elearning" ||
    rest.entry.fields.url === "/courses/elearning/"
  ) {
    return <OnlineCoursesListing courses={courses} {...rest} />;
  }

  const { pre, post } = courses.items.reduce(
    (c, a) => {
      if (
        a.fields &&
        a.fields.categories &&
        a.fields.categories.includes("online")
      ) {
        if (a.fields.data.active) {
          return {
            pre: c.pre,
            post: [...c.post, a],
          };
        } else {
          return {
            pre: [...c.pre, a],
            post: c.post,
          };
        }
      } else {
        return {
          pre: [],
          post: [...c.post, a],
        };
      }
    },
    { pre: [], post: [] }
  );

  const { data } = rest.entry.fields;

  if (data && data.seperated) {
    return <SeperatedListing courses={courses} {...rest} />;
  }

  const groupedCourses = post
    ?.filter((x) => !x.fields.hidden)
    .reduce(
      (a, item) => {
        const category = item.fields.categories?.[0] || "other";
        a[category] = a[category] || [];
        a[category].push(item);
        return a;
      },
      { other: [] }
    );

  return (
    <>
      <section className="container mx-auto relative bg-gray-50 pb-20 px-4 sm:px-6 lg:pt-0 lg:pb-28">
        <div className="absolute inset-0">
          <div className="bg-white h-1/3 sm:h-2/3"></div>
        </div>
        <div className="relative max-w-7xl mx-auto flex">
          <div className="hidden lg:block sticky lg:top-0 lg:mr-8 lg:w-1/5 lg:mt-6 lg:-ml-4">
            {/* <h3 className="text-lg font-extrabold text-gray-900 sm:tracking-tight mb-4">
              {cat}
            </h3> */}
            <ul className="space-y-3">
              {Object.keys(groupedCourses)
                .sort()
                .map((key) => {
                  const nKey =
                    key === "vtct"
                      ? "VTCT"
                      : key === "packages"
                      ? "Training Packages"
                      : key;

                  return (
                    <>
                      {key !== "other" ? (
                        <Link
                          href={`/courses/${key}`}
                          className="font-semibold block mt-3"
                        >
                          {replaceHyphensAndCapitalize(nKey)}
                        </Link>
                      ) : (
                        ""
                      )}
                      {groupedCourses[key]
                        .sort((a, b) =>
                          a.fields.title.localeCompare(b.fields.title)
                        )
                        .map((course) => {
                          return (
                            <li
                              key={`list-${course.sys.id}`}
                              className="text-xs"
                            >
                              <Link
                                href={course.fields.url}
                                className="hover:text-brand-500 transition-colors duration-150"
                              >
                                {course.fields.title}
                              </Link>
                            </li>
                          );
                        })}
                    </>
                  );
                })}
            </ul>
          </div>
          <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 xl:grid-cols-2 xl:gap-6 lg:max-w-none flex-1">
            {post && post.length > 0
              ? post
                  .filter((x) => !x.fields.hidden)
                  .map((course) => {
                    if (
                      course.fields &&
                      course.fields.type &&
                      course.fields.type === "Online Course"
                    ) {
                      return (
                        <OnlineCourseItem
                          key={`item-${course.sys.id}`}
                          course={course}
                          cat={cat}
                          setInterested={setInterested}
                        />
                      );
                    }
                    return (
                      <CourseItem
                        key={`item-${course.sys.id}`}
                        course={course}
                        rest={rest}
                        cat={cat}
                        session={session}
                        router={router}
                      />
                    );
                  })
              : null}
          </div>
        </div>
        {pre && pre.length > 0 ? (
          <div className="relative max-w-7xl mx-auto">
            <div className="my-12 md:my-16 mx-auto lg:max-w-none">
              <h3 className="text-xl font-extrabold text-gray-900 sm:text-3xl sm:tracking-tight lg:text-3xl">
                New Courses Coming Soon!
              </h3>
            </div>
            <div className="md:mt-6 mx-auto grid gap-12 md:grid-cols-2 lg:grid-cols-2 lg:max-w-none">
              {pre.map((course) => {
                if (
                  course.fields &&
                  course.fields.type &&
                  course.fields.type === "Online Course"
                ) {
                  return (
                    <OnlineCourseItem
                      key={`item-${course.sys.id}`}
                      course={course}
                      cat={cat}
                      setInterested={setInterested}
                    />
                  );
                }
                return (
                  <CourseItem
                    key={`item-${course.sys.id}`}
                    course={course}
                    rest={rest}
                    cat={cat}
                    session={session}
                    router={router}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
      {interested && (
        <Modal
          open={interested}
          close={() => setInterested(false)}
          xlarge
          title="Register Interest"
          subTitle={
            interested
              ? `Get notified when ${interested.fields.title} is available`
              : null
          }
        >
          <form
            action="https://manage.kmail-lists.com/subscriptions/subscribe?a=XxnURE&g=WgFGLa"
            method="post"
            target="_blank"
          >
            <div className="grid gap-4 grid-cols-1 my-4">
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Name</span>
                  <input
                    type="text"
                    // {...form.register("name", { required: true })}
                    name="FIRST_NAME"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your name"
                  />
                </label>
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Email</span>
                  <input
                    type="email"
                    // {...form.register("email", { required: true })}
                    name="EMAIL"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your email"
                  />
                </label>
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700">
                  <span>Phone number</span>
                  <input
                    type="tel"
                    // {...form.register("phone", { required: true })}
                    name="PHONE_NUMBER"
                    className="shadow-sm focus:ring-brand-500 focus:border-brand-600 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter your phone number"
                  />
                </label>
              </div>
              <div className="col-span-1 flex justify-end">
                <div className="w-64">
                  <button className="w-full flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-600 hover:bg-brand-900">
                    Register interest
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function replaceHyphensAndCapitalize(str) {
  return str
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
